/**
 * Счётчик длины — docs/phase-1/05-ui.md §4.1, docs/telegram.md §2, §9.
 *
 * Единиц три, и путать их нельзя:
 *   кодпоинты  — порог маршрутизации 4064, сервер меряет utf8_length();
 *   UTF-16     — единица offset'ов сущностей, она же `str.length` в JS;
 *   байты UTF-8 — предел тела запроса 32768, ошибка не транзиентная.
 *
 * Главный счётчик — кодпоинты. `str.length` завышал бы счёт на каждом эмодзи и заставил бы
 * отклонять валидные посты; расхождение двух метрик само по себе сигнал — в тексте есть
 * символы вне BMP, то есть возможен разрез суррогатной пары (§4.4).
 *
 * ЧИСЛА ПОД ДВУМЯ ТРАНСПОРТАМИ РАЗНЫЕ, и это не оформление. У обычного сообщения знаменатель —
 * порог 4064 кодпоинтов: перешагнув его, пост уходит расширенным сообщением и теряет карточку
 * превью. У расширенного знаменателя в кодпоинтах нет вовсе: `codePointLen(s) <= utf8Len(s)`
 * для любой строки, поэтому потолок 32768 кодпоинтов недостижим — раньше упрётся предел тела
 * запроса в 32768 БАЙТ. Обещать автору 32768 знаков значило бы соврать вдвое: кириллица весит
 * два байта на знак.
 *
 * ТРАНСПОРТ ЧИТАЕТСЯ, А НЕ ВЫВОДИТСЯ (19.09.2026). С политикой бренда «все посты — расширенным
 * сообщением» короткий пост законно уходит rich, и счётчик, выводящий способ по длине, сказал
 * бы «обычное сообщение, карточка снизу» про пост без карточки. `wireOf` берёт `method`, когда
 * он есть (сохранённая версия, выведенный транспорт набранной правки).
 *
 * ПРО БАЙТОВЫЙ ПОТОЛОК — ПОДСКАЗКОЙ, А НЕ АБЗАЦЕМ. Пока rich уходили только длинные посты и
 * посты с картинкой, абзац про 32 768 байт стоял под немногими; с политикой он встал бы под
 * каждым постом бренда. Видимой строкой он остаётся только на подходе к потолку, где он и
 * нужен (правило партии §1: объяснение — в подсказке, в теле — данные).
 */

import {
  codePointLen,
  limitValue,
  REQUEST_MAX_UTF8_BYTES,
  SEND_MESSAGE_MAX_CODEPOINTS,
  utf8Len,
  utf16Len,
} from '@smm/contracts';
import type { JSX } from 'react';
import styles from './PreviewPanel.module.css';
import { overLimit, type PlainPhraseInput, plainMessagePhrase, wireOf } from './validation.ts';

const TOOLTIP =
  '👨‍👩‍👧 — один видимый символ, но 5 кодпоинтов и 8 единиц UTF-16. ' +
  'Telegram считает кодпоинты, поэтому счётчик прыгает на 5, а не на 1.';

/** Объяснение байтового потолка — подсказка на «?» у расширенного сообщения. */
export const RICH_BYTES_NOTE =
  'Потолок расширенного сообщения меряется в байтах: 32 768 байт UTF-8, а не знаков. ' +
  'Кириллица весит два байта на знак, поэтому русского текста влезает около 16 000 знаков — ' +
  'упрётесь вы в байты. Превышение — жёсткая 400, повторять запрос бесполезно.';

/** С какой доли байтового потолка абзац про него становится видимым. */
const BYTES_NEAR = 0.9;

/**
 * Разряды разделяются НЕРАЗРЫВНЫМ пробелом: «1 240 / 4064» не имеет права переноситься
 * посреди числа. Своя функция, а не Intl.NumberFormat: формат обязан быть одинаковым в
 * браузере и в тесте, а Intl зависит от локали среды.
 */
export function group(n: number): string {
  const s = String(n);
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const fromEnd = s.length - i;
    if (i > 0 && fromEnd % 3 === 0) out += '\u00a0';
    out += s.charAt(i);
  }
  return out;
}

export function LengthMeter({ canonical }: { canonical: PlainPhraseInput }): JSX.Element {
  // Транспорт — подписанный `method`, когда он есть; иначе выводится тем же правилом, каким
  // его выберет заморозка (шапка файла).
  const rich = wireOf(canonical) === 'sendRichMessage';
  const threshold = limitValue(SEND_MESSAGE_MAX_CODEPOINTS);
  const byteCap = limitValue(REQUEST_MAX_UTF8_BYTES);
  const codepoints = codePointLen(canonical.text);
  const utf16 = utf16Len(canonical.text);
  const bytes = utf8Len(canonical.text);
  const cp = codepoints as unknown as number;
  const cu = utf16 as unknown as number;
  const b = bytes as unknown as number;
  const over = overLimit(canonical);

  return (
    <div className={styles['meter']}>
      <div className="lmeter">
        {rich ? (
          <>
            <span className="cnt num">{group(cp)}</span>
            <span className="unit">кодпоинтов</span>
            <span className="unit">
              · {group(b)} / {group(byteCap)} байт UTF-8
            </span>
            <span className="unit">· расширенное сообщение, карточки нет</span>
          </>
        ) : (
          <>
            <span className="cnt num">
              {group(cp)} / {threshold}
            </span>
            <span className="unit">кодпоинтов</span>
            <span className="unit">· {plainMessagePhrase(canonical)}</span>
          </>
        )}
        <span className={styles['hint']} title={rich ? `${RICH_BYTES_NOTE} ${TOOLTIP}` : TOOLTIP}>
          ?
        </span>
        {over ? <span className="pill st-err">лимит превышен</span> : null}
      </div>

      {cp === cu ? null : (
        <div className="micro">
          UTF-16: {group(cu)} — в тексте есть символы вне BMP. Порог считает кодпоинты, смещения
          сущностей — единицы UTF-16.
        </div>
      )}

      {rich ? (
        b > byteCap * BYTES_NEAR ? (
          <div className="micro">
            {b > byteCap
              ? `Текст весит ${group(b)} байт при потолке ${group(byteCap)} — сократите на ${group(b - byteCap)} байт.`
              : `До потолка ${group(byteCap)} байт осталось ${group(byteCap - b)}.`}{' '}
            {RICH_BYTES_NOTE}
          </div>
        ) : null
      ) : cp > threshold * 0.9 ? (
        <div className="micro">
          До порога {threshold} осталось {threshold - cp} кодпоинтов. Выше него пост уйдёт
          расширенным сообщением, и карточки превью ссылки у него не будет.
        </div>
      ) : null}
    </div>
  );
}
